import React, { useMemo } from 'react';

const IMPUESTO_RATE = 0.145;

export default function CosteoPreview({ manifest, packages, onExport }) {

  console.log('[CosteoPreview] manifest:', manifest);
  console.log('[CosteoPreview] packages:', packages);

  const rows = useMemo(() => {
    if (!packages || packages.length === 0) return [];

    const totalLbs = manifest?.total_lbs || 0;
    const flete = manifest?.flete || 0;

    return packages.map(pkg => {
      const items = pkg.items || [];

      const itemRows = items.map((item, idx) => {
        const valorUnitario =
          item.unit_price != null ? item.unit_price :
          item.valor_unitario != null ? item.valor_unitario :
          item.unit_value != null ? item.unit_value :
          item.price || 0;
        const cantidad = item.quantity != null ? item.quantity : (item.cantidad || 1);
        const totalItem = (parseFloat(valorUnitario) || 0) * (parseFloat(cantidad) || 0);

        return {
          isItem: true,
          consignatario: idx === 0 ? pkg.consignatario : '',
          pkgNumber: idx === 0 ? pkg.pkg_number : '',
          cantidad: parseFloat(cantidad) || 0,
          descripcion: item.description || item.descripcion || '',
          valorUnitario: parseFloat(valorUnitario) || 0,
          totalItem,
          isPlaceholder: !!item.is_placeholder,
        };
      });

      const valorFOB = items.reduce((sum, item) => {
        const price = item.unit_price != null ? item.unit_price :
          item.valor_unitario != null ? item.valor_unitario :
          item.unit_value != null ? item.unit_value :
          item.price || 0;
        const qty = item.quantity != null ? item.quantity : (item.cantidad || 1);
        return sum + ((parseFloat(price) || 0) * (parseFloat(qty) || 0));
      }, 0);

      const pesoLbs = parseFloat(pkg.peso_lbs) || 0;
      const fleteShare = totalLbs > 0 ? (pesoLbs / totalLbs) * flete : 0;
      const valorCIF = valorFOB + fleteShare;
      const impuesto = pkg.type === 'CASILLERO' ? 0 : valorCIF * IMPUESTO_RATE;
      const totalACobrar = fleteShare + impuesto;
      const hasZeros = valorFOB === 0;

      const subtotalRow = {
        isSubtotal: true,
        pesoLbs,
        valorFOB,
        fleteShare,
        valorCIF,
        impuesto,
        totalACobrar,
        hasZeros,
      };

      return { itemRows, subtotalRow, hasZeros, type: pkg.type };
    });
  }, [packages, manifest]);

  const grandTotal = useMemo(() => {
    const totalLbs = manifest?.total_lbs || 0;
    const flete = manifest?.flete || 0;
    let totalFOB = 0, totalImpuesto = 0, totalCobrar = 0;

    (packages || []).forEach(pkg => {
      const items = pkg.items || [];
      const valorFOB = items.reduce((sum, item) => {
        const price = item.unit_price != null ? item.unit_price :
          item.valor_unitario != null ? item.valor_unitario :
          item.unit_value != null ? item.unit_value :
          item.price || 0;
        const qty = item.quantity != null ? item.quantity : (item.cantidad || 1);
        return sum + ((parseFloat(price) || 0) * (parseFloat(qty) || 0));
      }, 0);
      const pesoLbs = parseFloat(pkg.peso_lbs) || 0;
      const fleteShare = totalLbs > 0 ? (pesoLbs / totalLbs) * flete : 0;
      const valorCIF = valorFOB + fleteShare;
      const impuesto = pkg.type === 'CASILLERO' ? 0 : valorCIF * IMPUESTO_RATE;
      totalFOB += valorFOB;
      totalImpuesto += impuesto;
      totalCobrar += fleteShare + impuesto;
    });

    return { totalFOB, totalImpuesto, totalCobrar };
  }, [packages, manifest]);

  const fmt = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;
  const hasAnyZeros = rows.some(r => r.hasZeros);

  if (!packages || packages.length === 0) {
    return (
      <div style={{ padding: '16px', color: '#6b7280', fontSize: '12px', textAlign: 'center' }}>
        Sin datos de costeo disponibles.
      </div>
    );
  }

  return (
    <div className="tile" style={{ marginTop: '24px' }}>
      <h3 className="tile-title mb-4">Preview de Costeo</h3>

      {/* Config header */}
      <div className="metric-strip mb-4">
        {[
          ['Total lbs', manifest?.total_lbs ?? '—'],
          ['Flete', fmt(manifest?.flete)],
          ['Adimex', fmt(manifest?.adimex)],
          ['Trámite', fmt(manifest?.tramite)],
          ['Guía', fmt(manifest?.guia)],
        ].map(([label, val]) => (
          <div key={label} className="metric-card">
            <div className="metric-label">{label}</div>
            <div className="metric-value" style={{ fontSize: '14px', marginTop: '2px', fontFamily: 'monospace' }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Zero warning banner */}
      {hasAnyZeros && (
        <div className="badge badge-hold w-full mb-4 p-3" style={{ fontSize: '12px', textAlign: 'center' }}>
          Algunos paquetes tienen valor FOB en cero — revisa los artículos antes de exportar.
        </div>
      )}

      {/* Table */}
      <div style={{
        overflowX: 'auto',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', minWidth: '900px' }}>
          <thead>
            <tr style={{ background: 'var(--text-main)' }}>
              {['Consignatario', 'Pkg#', 'Cant', 'Descripción', 'Val. Unit', 'Total $', 'Lbs', 'FOB', 'CIF', 'Impuesto', 'Flete', 'Total'].map(h => (
                <th key={h} style={{
                  padding: '8px 10px',
                  textAlign: h === 'Consignatario' || h === 'Descripción' ? 'left' : 'right',
                  fontWeight: '600',
                  fontSize: '9px',
                  color: 'var(--tile)',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: 'var(--border)' }}>
            {rows.map((pkg, pkgIdx) => (
              <React.Fragment key={pkgIdx}>
                {pkg.itemRows.map((row, rowIdx) => (
                  <tr key={rowIdx} style={{
                    background: pkgIdx % 2 === 0 ? 'transparent' : 'var(--bg)',
                  }}>
                    <td style={{ padding: '6px 10px', fontWeight: row.consignatario ? '600' : '400', color: 'var(--text-main)', whiteSpace: 'nowrap' }}>
                      {row.consignatario}
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace', color: 'var(--text-tertiary)' }}>
                      {row.pkgNumber}
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--text-secondary)' }}>
                      {row.isPlaceholder ? '' : row.cantidad}
                    </td>
                    <td style={{ padding: '6px 10px', color: 'var(--text-secondary)', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.descripcion}
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace', color: row.valorUnitario === 0 ? 'var(--amber)' : 'var(--text-main)' }}>
                      {row.isPlaceholder ? '' : fmt(row.valorUnitario)}
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace', color: 'var(--text-secondary)' }}>
                      {row.isPlaceholder ? '' : fmt(row.totalItem)}
                    </td>
                    <td colSpan={6}></td>
                  </tr>
                ))}
                {/* Subtotal row */}
                <tr style={{
                  background: pkg.hasZeros ? 'var(--amber-soft)' : 'var(--blue-soft)',
                  fontWeight: '600',
                }}>
                  <td style={{ padding: '6px 10px', fontSize: '9px', color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>↳ subtotal</td>
                  <td colSpan={5}></td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace', color: 'var(--text-main)' }}>
                    {pkg.subtotalRow.pesoLbs.toFixed(2)}
                  </td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace', color: pkg.hasZeros ? 'var(--amber)' : 'var(--blue-text)' }}>
                    {fmt(pkg.subtotalRow.valorFOB)}
                  </td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace', color: 'var(--text-main)' }}>
                    {fmt(pkg.subtotalRow.valorCIF)}
                  </td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace', color: 'var(--amber)' }}>
                    {pkg.type === 'CASILLERO' ? '—' : fmt(pkg.subtotalRow.impuesto)}
                  </td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace', color: 'var(--text-secondary)' }}>
                    {fmt(pkg.subtotalRow.fleteShare)}
                  </td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace', color: 'var(--text-main)', fontWeight: '700' }}>
                    {fmt(pkg.subtotalRow.totalACobrar)}
                  </td>
                </tr>
              </React.Fragment>
            ))}
            {/* Grand total */}
            <tr style={{ background: 'var(--green-soft)', fontWeight: '700' }}>
              <td colSpan={7} style={{ padding: '8px 10px', fontSize: '10px', color: 'var(--green)', textTransform: 'uppercase' }}>TOTAL GENERAL</td>
              <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace', color: 'var(--green)' }}>{fmt(grandTotal.totalFOB)}</td>
              <td></td>
              <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace', color: 'var(--green)' }}>{fmt(grandTotal.totalImpuesto)}</td>
              <td></td>
              <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace', color: 'var(--green)' }}>{fmt(grandTotal.totalCobrar)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Export button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
        <button
          onClick={onExport}
          className="btn btn-primary"
          style={{ background: 'var(--green)', padding: '10px 24px', fontSize: '12px' }}
        >
          Exportar a Excel
        </button>
      </div>
    </div>
  );
}
